import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  X, User, Phone, Briefcase, Mail, Shield, Clock, Building2, Hash,
  PhoneCall, Calendar, CreditCard, Save, Loader2, AlertCircle, Plus, Trash2,
  GraduationCap, Award, Upload,
} from 'lucide-react';

import tenant from '../../config/tenant';
import { RELATIONSHIP_OPTS } from '../../constants/relationshipOptions';
import { isMasterApproved } from '../../utils/masterApproval';
import { NEW_BANK_IS_APPROVED, BANK_APPROVED, BANK_PENDING, isBankActive, isBankApproved, isBankPrimary } from '../../utils/employeeBankApproval';
import IsoDateInput from '../../components/common/Isodateinput';
import ExperienceInput from '../../components/common/Experienceinput';
import {
  sanitiseEpf, checkEpf,
  sanitiseNic, normaliseNic, checkNic,
  checkDateOfBirth, maxDobIso,
  checkDateOfJoining, todayIso,
  sanitisePhone, checkPhone,
  checkEmail,
  parseExperience, checkExperience,
  toIso,
} from '../../utils/employeeValidation';
import { getEmployeeProfilePicUrl } from '../../utils/employeeUtils';
import employeeService from '../../services/EmployeeService';
import bankService from '../../services/BankService';
import bankBranchService from '../../services/BankBranchService';
import designationService from '../../services/DesignationService';
import departmentService from '../../services/DepartmentService';
import locationService from '../../services/LocationService';
import gradeService from '../../services/GradeService';
import shiftService from '../../services/ShiftService';
import employeeCategoryService from '../../services/EmployeeCategoryService';
import { saveEmployeeImage } from '../../store/slices/imageSlice';
import { getLoggedInServiceNo, getLoggedInUserId } from '../../utils/masterDataUtils';

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = () => resolve(reader.result);
  reader.onerror = (err) => reject(err);
});

// ─── Helpers ──────────────────────────────────────────────────────────────────
const d = (v) => {
  if (v === null || v === undefined || v === '' || v === 'null' || v === 'undefined') return null;
  const s = String(v).trim();
  return s === '' || s === '0' ? null : s;
};

const fmtDate = (v) => toIso(d(v));

const RELIGION_OPTS = [
  { value: 'B', label: 'Buddhism' }, { value: 'C', label: 'Christian' },
  { value: 'H', label: 'Hindu' }, { value: 'I', label: 'Islam' },
  { value: 'O', label: 'Other' },
];
const MARITAL_OPTS = [
  { value: 'S', label: 'Single' }, { value: 'M', label: 'Married' },
  { value: 'D', label: 'Divorced' }, { value: 'W', label: 'Widowed' },
];
const GENDER_OPTS = [{ value: 'M', label: 'Male' }, { value: 'F', label: 'Female' }];
const BLOOD_OPTS = [
  { value: '1', label: 'A+' }, { value: '2', label: 'A-' }, { value: '3', label: 'B+' },
  { value: '4', label: 'B-' }, { value: '5', label: 'AB+' }, { value: '6', label: 'AB-' },
  { value: '7', label: 'O+' }, { value: '8', label: 'O-' },
];

const STATUS_OPTS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

/* Sent as p_InactiveReason — the reason TEXT itself, not an ID. */
const INACTIVE_REASONS = [
  'Resignation',
  'Termination',
  'Retirement',
  'Contract Completed',
  'Absconded / Absent Without Notice',
  'Dismissal',
  'Probation Not Confirmed',
  'Redundancy',
  'Transfer',
  'Deceased',
  'Other',
];
const INACTIVE_REASON_OPTS = INACTIVE_REASONS.map((r) => ({ value: r, label: r }));

const resolveInactiveReason = (v) => {
  const raw = String(v ?? '').trim();
  if (!raw) return '';
  const asIndex = Number(raw);
  if (Number.isInteger(asIndex) && asIndex >= 1 && asIndex <= INACTIVE_REASONS.length) {
    return INACTIVE_REASONS[asIndex - 1];
  }
  return raw;
};

function calcYears(date) {
  if (!date) return null;
  const s = new Date(date);
  if (isNaN(s.getTime())) return null;
  const now = new Date();
  let y = now.getFullYear() - s.getFullYear();
  if (now.getMonth() - s.getMonth() < 0 ||
     (now.getMonth() - s.getMonth() === 0 && now.getDate() < s.getDate())) y--;
  return y >= 0 ? y : null;
}

function SH({ label, icon: Icon }) {
  return (
    <div className="flex items-center gap-2 mb-2 mt-4 first:mt-0">
      {Icon && <Icon size={12} className="text-primary-600" />}
      <span className="text-[10px] font-bold text-primary-700 uppercase tracking-wider">{label}</span>
      <div className="flex-1 h-px bg-primary-100" />
    </div>
  );
}

function FI({ label, value, onChange, type = 'text', options, mono = false, error, placeholder, render }) {

  const base = `w-full h-9 text-xs rounded-lg border px-2.5 outline-none transition-colors
    ${error ? 'border-red-300 bg-red-50 focus:border-red-400'
            : 'border-gray-200 bg-white text-gray-800 focus:border-primary-500'}
    ${mono ? 'font-mono' : ''}`;

  return (
    <div className="py-1.5">
      <label className="text-[11px] text-gray-500 font-medium block mb-1">{label}</label>

      {render ? render(base) : options ? (
        <select className={base} value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
          <option value="">—</option>
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : (
        <input
          className={base}
          type={type}
          value={value ?? ''}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      {error && <p className="text-[10px] text-red-500 mt-0.5">{error}</p>}
    </div>
  );
}

// ─── Tabs ──────────────────────────────────────────────────────────────────
const TABS = [
  { key: 'overview',   label: 'Overview',   icon: User },
  { key: 'contact',    label: 'Contact',    icon: Phone },
  { key: 'employment', label: 'Employment', icon: Briefcase },
  { key: 'bank',       label: 'Bank',       icon: CreditCard },
  { key: 'qualify',    label: 'Education',  icon: GraduationCap },
];


const HEADER_GRADIENT = 'bg-gradient-to-br from-primary-600 to-primary-700';

// Bank active flag from GetEmployeebyID (Banks[].HEB_IS_ACTIVE "True"/"False").
const isBankActiveFlag = isBankActive;

// Build form state
function buildFormState(e) {
  return {
    // Personal
    HED_TITLE:            d(e.HED_TITLE) || d(e.p_title) || '',
    HED_FIRST_NAME:       d(e.HED_FIRST_NAME) || d(e.p_first_name) || '',
    HED_MIDDLE_NAME:      d(e.HED_MIDDLE_NAME) || '',
    HED_LAST_NAME:        d(e.HED_LAST_NAME) || d(e.p_last_name) || '',
    HED_REPORT_NAME:      d(e.HED_REPORT_NAME) || d(e.p_report_name) || '',
    HED_INITIALS:         d(e.HED_INITIALS) || d(e.p_initials) || '',
    HED_GENDER:           d(e.HED_GENDER) || d(e.p_sex) || '',
    HED_DATE_OF_BIRTH:    fmtDate(e.HED_DATE_OF_BIRTH || e.p_birth_date),
    HED_MARITAL_STATUS:   d(e.HED_MARITAL_STATUS) || d(e.p_marital_status) || '',
    HED_RELIGION:         d(e.HED_RELIGION) || d(e.p_religion) || '',
    HED_BLOOD_GROUP:      d(e.HED_BLOOD_GROUP) || d(e.p_blood_group) || '',

    // Identification
    HED_NIC_NO:           d(e.HED_NIC_NO) || d(e.p_nid) || '',
    HED_EPF_NO:           d(e.HED_EPF_NO) || d(e.p_epf) || '',
    HED_ETF_NO:           d(e.HED_ETF_NO) || '',
    HED_PASSPORT_NO:      d(e.HED_PASSPORT_NO) || d(e.p_passport) || '',

    // Contact
    HED_MOBILE_NO:        d(e.HED_MOBILE_NO) || d(e.p_mobile) || '',
    HED_HOME_NO:          d(e.HED_HOME_NO) || d(e.p_home_tel) || '',
    HED_OFFICE_NO:        d(e.HED_OFFICE_NO) || d(e.p_office_tel) || '',
    HED_PERSONAL_EMAIL:   d(e.HED_PERSONAL_EMAIL) || d(e.p_personal_email) || '',
    HED_COMPANY_EMAIL:    d(e.HED_COMPANY_EMAIL) || d(e.p_email) || '',

    // Employment
    HED_DATE_OF_JOINING:  fmtDate(e.HED_DATE_OF_JOINING || e.p_recruitment_date),
    HED_CONFIRMATION_DATE: fmtDate(e.HED_CONFIRMATION_DATE || e.p_permanent_date),
    HED_DESIGNATION_NAME: d(e.HED_DESIGNATION_NAME) || d(e.p_designation) || '',
    HDD_DEPARTMENT_NAME:  d(e.HDD_DEPARTMENT_NAME) || d(e.p_department_code) || '',
    HED_COSTCENTER:       d(e.HED_COSTCENTER) || d(e.p_location_code) || '',
    HED_NOTICE_PERIOD_DAYS: d(e.HED_NOTICE_PERIOD_DAYS) || '',
    HED_OT_RATE:          d(e.HED_OT_RATE) || '',
    HED_IS_ACTIVE: e.HED_IS_ACTIVE !== undefined
      ? Boolean(e.HED_IS_ACTIVE)
      : (!e.p_inactive_type || e.p_inactive_type === '' || e.p_inactive_type === '0'),

    HED_LEAVING_REASON:  resolveInactiveReason(d(e.HED_LEAVING_REASON) || d(e.p_inactive_type)),
    HED_LEAVING_DATE:    toIso(d(e.HED_LEAVING_DATE)),
    HED_REMARKS:          d(e.HED_REMARKS) || d(e.p_remarks) || '',
    HED_TOTAL_EXPERIANCE: d(e.HED_TOTAL_EXPERIANCE) || '',

    HED_REPORTING_EMPLOYEE_ID: String(
      d(e.HED_REPORTING_EMPLOYEE_ID) || d(e.HedReportingEmployeeId) ||
      d(e.ReportingEmployeeId) || d(e.reportingEmployeeId) || ''
    ),

    // Emergency contacts
    Contacts: (e.Contacts || []).map((c, i) => ({
      _key: c.HEC_CONTACT_ID || `c-${i}`,
      HEC_CONTACT_ID: c.HEC_CONTACT_ID,
      HEC_CONTACT_NAME: d(c.HEC_CONTACT_NAME) || '',
      HEC_CONTACT_NUMBER: d(c.HEC_CONTACT_NUMBER) || '',
      HEC_RELATIONSHIP: d(c.HEC_RELATIONSHIP) || '',
      HEC_ADDRESS: d(c.HEC_ADDRESS) || '',
      HEC_CONTACT_TYPE: d(c.HEC_CONTACT_TYPE) || 'Emergency',
      _isNew: false,
      _isDeleted: false,
    })),
    // Bank accounts
    Banks: (e.Banks || []).map((b, i) => ({
      _key: b.HEB_EMPLOYEE_BANK_ID || `b-${i}`,
      HEB_EMPLOYEE_BANK_ID: b.HEB_EMPLOYEE_BANK_ID,
      HBD_BANK_NAME: d(b.HBD_BANK_NAME) || '',
      HBB_BRANCH_NAME: d(b.HBB_BRANCH_NAME) || '',
      HEB_ACCOUNT_NAME: d(b.HEB_ACCOUNT_NAME) || '',
      HEB_ACCOUNT_NUMBER: d(b.HEB_ACCOUNT_NUMBER) || '',
      HEB_ACCOUNT_TYPE: d(b.HEB_ACCOUNT_TYPE) || 'Savings',
      HEB_IS_PRIMARY: isBankPrimary(b),
      HBD_IS_ACTIVE: isBankActiveFlag(b),
      _wasActive: isBankActiveFlag(b),   // state when loaded — re-activation needs approval
      // HBD_BANK_ID: d(b.HBD_BANK_ID) || '',
      // HBB_BRANCH_ID: d(b.HBB_BRANCH_ID) || '',
      HBD_BANK_ID: d(b.HEB_BANK_ID) || '',
      HBB_BRANCH_ID: d(b.HEB_BRANCH_ID) || '',
      _isNew: false,
      _isDeleted: false,
    })),
    // Qualifications
    Qualifications: (e.Qualifications || []).map((q, i) => ({
      _key: q.HEQ_QUALIFICATION_ID || `q-${i}`,
      HEQ_QUALIFICATION_ID: q.HEQ_QUALIFICATION_ID,
      HEQ_QUALIFICATION_NAME: d(q.HEQ_QUALIFICATION_NAME) || '',
      HEQ_FIELD_OF_STUDY: d(q.HEQ_FIELD_OF_STUDY) || '',
      HEQ_INSTITUTE: d(q.HEQ_INSTITUTE) || '',
      HEQ_RESULT: d(q.HEQ_RESULT) || '',
      HEQ_GRADE: d(q.HEQ_GRADE) || '',
      HEQ_GPA: d(q.HEQ_GPA) || '',
      HEQ_QUALIFICATION_TYPE_ID: d(q.HEQ_QUALIFICATION_TYPE_ID) || 1,
      _isNew: false,
      _isDeleted: false,
    })),

    // Employment-related dropdown IDs
    HED_DESIGNATION_ID: d(e.HED_DESIGNATION_ID) || '',
    HDD_DEPARTMENT_ID: d(e.HED_DEPARTMENT_ID) || d(e.HedDepartmentId) || d(e.DepartmentId) || d(e.departmentId) || d(e.HDD_DEPARTMENT_ID) || d(e.p_hedDepartmentId) || d(e.HDD_DEPARTMENT_CODE) || d(e.HDD_DEP_ID) || d(e.HDD_DEP_CODE) || d(e.p_department_code) || '', 
    HDD_DEPARTMENT_NAME: d(e.HDD_DEPARTMENT_NAME) || d(e.HDD_DEP_NAME) || d(e.p_department_name) || d(e.p_department_code) || '',
    HED_LOCATION_ID: d(e.HED_LOCATION_ID) || d(e.HLD_LOCATION_ID) || d(e.HLD_LOC_ID) || d(e.p_location_id) || d(e.p_location_code) || d(e.HED_COSTCENTER) || '',
    HED_GRADE_ID: d(e.HED_GRADE_ID) || '',
    HED_SHIFT_ID: d(e.HED_SHIFT_ID) || '',
    HED_EMPLOYMENT_CATEGORY_ID: d(e.HED_EMPLOYMENT_CATEGORY_ID) || d(e.HedEmploymentCategoryId) || d(e.EmploymentCategoryId) || d(e.employmentCategoryId) || d(e.p_hedEmploymentCategoryId) || d(e.HEC_EMPLOYMENT_CATEGORY_ID) || '',
    HEC_CATEGORY_NAME: d(e.HEC_CATEGORY_NAME) || d(e.EmploymentCategoryName) || d(e.employmentCategoryName) || d(e.p_cat_name) || d(e.p_cat_code) || '',
  };
}

// ─── Validation ────────────────────────────────────────────────────────────
function validate(form) {
  const errs = {};
  if (!form.HED_FIRST_NAME.trim()) errs.HED_FIRST_NAME = 'First name is required';
  if (!form.HED_LAST_NAME.trim())  errs.HED_LAST_NAME = 'Last name is required';

  /* The same rules the Add form applies — see utils/employeeValidation.js.
     Both screens write to the same columns, so they have to agree. */
  const dobErr = checkDateOfBirth(form.HED_DATE_OF_BIRTH);
  if (dobErr) errs.HED_DATE_OF_BIRTH = dobErr;

  const epfErr = checkEpf(form.HED_EPF_NO);
  if (epfErr) errs.HED_EPF_NO = epfErr;

  const nicErr = checkNic(form.HED_NIC_NO, { required: false });
  if (nicErr) errs.HED_NIC_NO = nicErr;

  const dojErr = checkDateOfJoining(form.HED_DATE_OF_JOINING, { required: false });
  if (dojErr) errs.HED_DATE_OF_JOINING = dojErr;

  const personalEmailErr = checkEmail(form.HED_PERSONAL_EMAIL, 'Personal email');
  if (personalEmailErr) errs.HED_PERSONAL_EMAIL = personalEmailErr;

  const companyEmailErr = checkEmail(form.HED_COMPANY_EMAIL, 'Company email');
  if (companyEmailErr) errs.HED_COMPANY_EMAIL = companyEmailErr;

  const mobileErr = checkPhone(form.HED_MOBILE_NO, 'Mobile no');
  if (mobileErr) errs.HED_MOBILE_NO = mobileErr;
  const homeErr = checkPhone(form.HED_HOME_NO, 'Home phone');
  if (homeErr) errs.HED_HOME_NO = homeErr;
  const officeErr = checkPhone(form.HED_OFFICE_NO, 'Office phone');
  if (officeErr) errs.HED_OFFICE_NO = officeErr;

  const expParts = parseExperience(form.HED_TOTAL_EXPERIANCE);
  const expErr = checkExperience(expParts.years, expParts.months);
  if (expErr) errs.HED_TOTAL_EXPERIANCE = expErr;

  // Emergency contact numbers follow the same rule.
  (form.Contacts || []).forEach((c, i) => {
    if (c._isDeleted) return;
    const err = checkPhone(c.HEC_CONTACT_NUMBER, 'Contact number');
    if (err) errs[`contact_${i}_number`] = err;
  });


  if (!form.HED_IS_ACTIVE) {
    if (!String(form.HED_LEAVING_DATE || '').trim()) {
      errs.HED_LEAVING_DATE = 'Select the leaving date';
    } else {
      const leftErr = checkDateOfJoining(form.HED_LEAVING_DATE, { required: false });
      if (leftErr) errs.HED_LEAVING_DATE = leftErr.replace('Date of joining', 'Leaving date');
    }
  }
  if (!form.HED_IS_ACTIVE && !String(form.HED_LEAVING_REASON || '').trim())
    errs.HED_LEAVING_REASON = 'Select a reason for marking this employee inactive';
  return errs;
}

// ─── Main Component ─────────────────────────────────────────────────────────
export default function EmployeeEditModal({
  employee,
  isOpen,
  onClose,
  onSave,
  saving = false,
  saveError = null,
}) {
  const dispatch = useDispatch();
  const images = useSelector((s) => s.images?.images || {});
  const [activeTab, setActiveTab] = useState('overview');
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});
  const [dirty, setDirty] = useState(false);
  const [localSaving, setLocalSaving] = useState(false);
  const [localSaveError, setLocalSaveError] = useState(null);

  // ─── File state for documents ──────────────────────────────────────────────
  const [files, setFiles] = useState({
    profilePic: null,
    nicDocument: null,
    birthCertificate: null,
    qualificationCertificate: null,
    traineeCertificate: null,
    otherCertificate: null,
  });

  const setFile = (key, file) => {
    setFiles(f => ({ ...f, [key]: file }));
    setDirty(true);
  };

  const [banks, setBanks] = useState([]);
  const [branches, setBranches] = useState([]);
  const [bankLoading, setBankLoading] = useState(false);

  const [designations, setDesignations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [grades, setGrades] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [reportingOptions, setReportingOptions] = useState([]);
  const [employmentLoading, setEmploymentLoading] = useState([]);

  const FIELD_MAP = {
    HED_TITLE:              'p_hedTitle',
    HED_FIRST_NAME:         'p_hedFirstName',
    HED_MIDDLE_NAME:        'p_hedMiddleName',
    HED_LAST_NAME:          'p_hedLastName',
    HED_REPORT_NAME:        'p_hedReportName',
    HED_INITIALS:           'p_hedInitials',
    HED_GENDER:             'p_hedGender',
    HED_DATE_OF_BIRTH:      'p_hedDateOfBirth',
    HED_MARITAL_STATUS:     'p_hedMaritalStatus',
    HED_RELIGION:           'p_hedReligion',
    HED_BLOOD_GROUP:        'p_hedBloodGroup',
    HED_NIC_NO:             'p_hedNicNo',
    HED_EPF_NO:             'p_hedEpfNo',
    HED_ETF_NO:             'p_hedEtfNo',
    HED_PASSPORT_NO:        'p_hedPassportNo',
    HED_MOBILE_NO:          'p_hedMobileNo',
    HED_HOME_NO:            'p_hedHomeNo',
    HED_OFFICE_NO:          'p_hedOfficeNo',
    HED_PERSONAL_EMAIL:     'p_hedPersonalEmail',
    HED_COMPANY_EMAIL:      'p_hedCompanyEmail',
    HED_DATE_OF_JOINING:    'p_hedDateOfJoining',
    HED_CONFIRMATION_DATE:  'p_hedConfirmationDate',
    // HED_DESIGNATION_NAME:   'p_hedDesignationName',
    HDD_DEPARTMENT_ID:    'p_hedDepartmentId',
    HED_DESIGNATION_ID:     'p_hedDesignationId',
    HED_LOCATION_ID:        'p_hedLocationId',
    HED_GRADE_ID:           'p_hedGradeId',
    HED_SHIFT_ID:           'p_hedShiftId',
    HED_EMPLOYMENT_CATEGORY_ID:     'p_hedEmploymentCategoryId',

    HED_COSTCENTER:         'p_hedCostCenter',
    HED_NOTICE_PERIOD_DAYS: 'p_hedNoticePeriodDays',
    HED_OT_RATE:            'p_OT',
    HED_IS_ACTIVE:          'p_hedIsActive',
    HED_LEAVING_REASON:    'p_hedLeavingReason',
    HED_LEAVING_DATE:      'p_hedLeavingDate',
    HED_REMARKS:            'p_hedRemarks',
    HED_TOTAL_EXPERIANCE:   'p_hedTotalExperiance',
    HED_REPORTING_EMPLOYEE_ID: 'p_hedReportingEmployeeId',
  };

  useEffect(() => {
    if (isOpen && employee) {
      setForm(buildFormState(employee));
      setErrors({});
      setDirty(false);
      setActiveTab('overview');
      // Reset file inputs when modal opens
      setFiles({ profilePic: null, nicDocument: null, birthCertificate: null });
    }
  }, [isOpen, employee]);

  const handleKey = useCallback((ev) => {
    if (ev.key === 'Escape' && !saving && !localSaving) onClose();
  }, [onClose, saving, localSaving]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKey);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKey]);

  const set = (key) => (val) => {
    setForm((f) => ({ ...f, [key]: val }));
    setDirty(true);
  };


  const setStatus = (val) => {
    const isActive = val === 'active';
    setForm((f) => ({
      ...f,
      HED_IS_ACTIVE: isActive,
      HED_LEAVING_REASON: isActive ? '' : f.HED_LEAVING_REASON,
      HED_LEAVING_DATE: isActive ? '' : f.HED_LEAVING_DATE,
    }));
    setDirty(true);
  };

  // ─── Contact helpers ─────────────────────────────────────────────────────
  const setContact = (idx, key) => (val) => {
    setForm((f) => {
      const list = [...f.Contacts];
      list[idx] = { ...list[idx], [key]: val };
      return { ...f, Contacts: list };
    });
    setDirty(true);
  };

  const addContact = () =>
    setForm((f) => ({
      ...f,
      Contacts: [
        ...f.Contacts,
        {
          _key: `new-${Date.now()}`,
          HEC_CONTACT_NAME: '',
          HEC_CONTACT_NUMBER: '',
          HEC_RELATIONSHIP: '',
          HEC_ADDRESS: '',
          HEC_CONTACT_TYPE: 'Emergency',
          _isNew: true,
          _isDeleted: false,
        },
      ],
    }));

  const removeContact = (idx) => {
    if (form.Contacts[idx]?.HEC_CONTACT_ID) return;
    setForm((f) => ({ ...f, Contacts: f.Contacts.filter((_, i) => i !== idx) }));
  };

  // ─── Bank helpers ────────────────────────────────────────────────────────
  const setBank = (idx, key) => (val) => {
    setForm((f) => {
      let list = [...f.Banks];
      list[idx] = { ...list[idx], [key]: val };
      // Only one primary account per employee: ticking one unticks the rest
      // (each of those is then saved with p_hebIsPrimary = false).
      if (key === 'HEB_IS_PRIMARY' && val === true) {
        list = list.map((b, i) => (i === idx || b._isDeleted ? b : { ...b, HEB_IS_PRIMARY: false }));
      }
      return { ...f, Banks: list };
    });
    if (key === 'HEB_IS_PRIMARY') setErrors((e) => ({ ...e, banks_primary: undefined }));
    setDirty(true);
  };

  const addBank = () =>
    setForm((f) => ({
      ...f,
      Banks: [
        ...f.Banks,
        {
          _key: `new-${Date.now()}`,
          HBD_BANK_NAME: '',
          HBB_BRANCH_NAME: '',
          HEB_ACCOUNT_NAME: '',
          HEB_ACCOUNT_NUMBER: '',
          HEB_ACCOUNT_TYPE: 'Savings',
          HEB_IS_PRIMARY: false,
          HBD_IS_ACTIVE: true,
          HBD_BANK_ID: '',
          HBB_BRANCH_ID: '',
          _isNew: true,
          _isDeleted: false,
        },
      ],
    }));


  const removeBank = (idx) => {
    if (form.Banks[idx]?.HEB_EMPLOYEE_BANK_ID) return;
    setForm((f) => ({ ...f, Banks: f.Banks.filter((_, i) => i !== idx) }));
  };

  const extractList = (res) => {
    if (!res) return [];

    const data = res?.data ?? res;

    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.ResultSet)) return data.ResultSet;
    if (Array.isArray(data.resultSet)) return data.resultSet;
    if (Array.isArray(data.Result)) return data.Result;
    if (Array.isArray(data.result)) return data.result;
    if (Array.isArray(data.Data)) return data.Data;
    if (Array.isArray(data.data)) return data.data;

    return [];
  };

  const pick = (obj, hints, fallback = '') => {
    if (!obj || typeof obj !== 'object') return fallback;

    for (const h of hints) {
      if (
        obj[h] !== undefined &&
        obj[h] !== null &&
        obj[h] !== ''
      ) {
        return obj[h];
      }
    }

    const objKeys = Object.keys(obj).map(k => ({
      raw: k,
      norm: String(k)
        .replace(/[^a-zA-Z0-9]/g, '')
        .toUpperCase(),
    }));

    for (const h of hints) {
      const hNorm = String(h)
        .replace(/[^a-zA-Z0-9]/g, '')
        .toUpperCase();

      if (!hNorm) continue;

      let match = objKeys.find(k => k.norm === hNorm);

      if (!match) {
        match = objKeys.find(k => k.norm.endsWith(hNorm));
      }

      if (!match && hNorm.length >= 3) {
        match = objKeys.find(k => k.norm.includes(hNorm));
      }

      if (
        match &&
        obj[match.raw] !== undefined &&
        obj[match.raw] !== null &&
        obj[match.raw] !== ''
      ) {
        return obj[match.raw];
      }
    }

    return fallback;
  };

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;

    const loadBankData = async () => {
      setBankLoading(true);

      try {
        const [
          designationRes,
          departmentRes,
          locationRes,
          gradeRes,
          shiftRes,
          categoryRes,
          bankRes, 
          branchRes,
          reportingRes,
        ] = await Promise.all([
          designationService.GetDesignationList().catch(() => null),
          departmentService.GetDepartmentList().catch(() => null),
          locationService.GetLocationList().catch(() => null),
          gradeService.GetGradeList().catch(() => null),
          shiftService.GetShiftList().catch(() => null),
          employeeCategoryService.GetEmployeeCategoryList().catch(() => null),
          bankService.GetBankList().catch(() => null),
          bankBranchService.getBankBranchList().catch(() => null),
          employeeService.GetEmployeeList().catch(() => null),
        ]);

        if (cancelled) return;

        console.log('[EmployeeEditModal] GetBanks response:', bankRes);
        console.log('[EmployeeEditModal] GetAllBranch response:', branchRes);

        const designationList = extractList(designationRes);
        const departmentList = extractList(departmentRes);
        const locationList = extractList(locationRes);
        const gradeList = extractList(gradeRes);
        const shiftList = extractList(shiftRes);
        const categoryList = extractList(categoryRes);

        // Only approved master records (HBD_IS_APPROVED / HBB_IS_APPROVED = "1")
        // are selectable — a bank still awaiting approval must not be assignable.
        const bankList = extractList(bankRes).filter(isMasterApproved);
        const branchList = extractList(branchRes).filter(isMasterApproved);

        console.log('[EmployeeEditModal] bankList:', bankList);
        console.log('[EmployeeEditModal] branchList:', branchList);

        const bankOptions = bankList
          .map((b) => {
            const id = pick(b, [
              'HBD_BANK_ID',
              'BankId',
              'bankId',
              'BankID',
              'BankCode',
            ]);

            const name = pick(b, [
              'HBD_BANK_NAME',
              'BankName',
              'bankName',
              'BankDesc',
            ]);

            return id
              ? {
                  value: String(id),
                  label: String(name || id),
                }
              : null;
          })
          .filter(Boolean);

        const branchOptions = branchList
          .map((b) => {
            const id = pick(b, [
              'HBB_BRANCH_ID',
              'BranchId',
              'branchId',
              'BranchID',
              'BranchCode',
            ]);

            const name = pick(b, [
              'HBB_BRANCH_NAME',
              'BranchName',
              'branchName',
              'BranchDesc',
            ]);

            const code = pick(b, [
              'HBB_BRANCH_CODE',
              'BranchCode',
              'branchCode',
            ]);

            const bankId = pick(b, [
              'HBB_BANK_ID',
              'BankId',
              'bankId',
              'BankID',
            ]);

            return id
              ? {
                  value: String(id),
                  label: `${name || id}${code ? ` (${code})` : ''}`,
                  bankId: String(bankId),
                }
              : null;
          })
          .filter(Boolean);

        console.log('[EmployeeEditModal] bankOptions:', bankOptions);
        console.log('[EmployeeEditModal] branchOptions:', branchOptions);

        setBanks(bankOptions);
        setBranches(branchOptions);

        /* Reporting officer — the same "Name (STAFFID)" label the Add form
           shows. The employee being edited is left out: nobody reports to
           themselves. */
        const selfId = String(
          employee?.HED_EMPLOYEE_ID ?? employee?.hedEmployeeId ?? employee?.EmployeeId ?? ''
        );
        setReportingOptions(
          extractList(reportingRes).map((r) => {
            const id = pick(r, ['HED_EMPLOYEE_ID', 'HedEmployeeId', 'EmployeeId', 'hedEmployeeId', 'employeeId', 'Id', 'id']);
            if (!id || String(id) === selfId) return null;
            const svcNo = pick(r, ['HED_SERVICE_NO', 'HedServiceNo', 'ServiceNo', 'serviceNo', 'p_ced_service_no']);
            const reportName = pick(r, ['HED_REPORT_NAME', 'HedReportName', 'ReportName', 'reportName', 'p_report_name']);
            const first = pick(r, ['HED_FIRST_NAME', 'HedFirstName', 'FirstName', 'firstName', 'p_first_name']);
            const last = pick(r, ['HED_LAST_NAME', 'HedLastName', 'LastName', 'lastName', 'p_last_name']);
            const name = reportName || `${first} ${last}`.trim() || String(id);
            return { value: String(id), label: `${name}${svcNo ? ` (${svcNo})` : ''}` };
          }).filter(Boolean)
        );

        setDesignations(
          toOpts(
            designationList,
            [
              'DesignationId',
              'designationId',
              'HDS_DESIGNATION_ID',
              'HDS_DESIGNATION_CODE',
              'p_des_code',
              'DesignationCode',
            ],
            [
              'DesignationName',
              'designationName',
              'HDS_DESIGNATION_NAME',
              'HDS_DESIGNATION_DESC',
              'p_des_name',
              'DesignationDesc',
            ]
          )
        );
        setDepartments(
          toOpts(
            departmentList,
            [
              'DepartmentId',
              'departmentId',
              'HDD_DEPARTMENT_ID',
              'HDD_DEP_ID',
              'HDD_DEP_CODE',
              'p_department_code',
              'DepartmentCode',
              'code',
              'id',
            ],
            [
              'DepartmentName',
              'departmentName',
              'HDD_DEPARTMENT_NAME',
              'HDD_DEP_NAME',
              'HDD_DEP_DESC',
              'p_department_name',
              'DepartmentDesc',
              'name',
              'description',
            ]
          )
        );
        setLocations(
          toOpts(
            locationList,
            [
              'LocationId',
              'locationId',
              'HLD_LOCATION_ID',
              'HLD_LOC_ID',
              'HLD_LOC_CODE',
              'p_location_code',
              'p_loc_code',
            ],
            [
              'LocationName',
              'locationName',
              'HLD_LOCATION_NAME',
              'HLD_LOC_NAME',
              'HLD_LOC_DESC',
              'p_location_name',
              'p_loc_name',
            ]
          )
        );

        setGrades(
          toOpts(
            gradeList,
            [
              'GradeId',
              'gradeId',
              'HGC_GRADE_ID',
              'HGC_GRADE_CODE',
              'p_grade_code',
              'p_initial_grade',
            ],
            [
              'GradeName',
              'gradeName',
              'HGC_GRADE_NAME',
              'HGC_GRADE_DESC',
              'p_grade_name',
            ]
          )
        );

        setShifts(
          toOpts(
            shiftList,
            [
              'ShiftId',
              'shiftId',
              'HSC_SHIFT_ID',
              'HSC_SHIFT_CODE',
              'p_shift_code',
            ],
            [
              'ShiftName',
              'shiftName',
              'HSC_SHIFT_NAME',
              'HSC_SHIFT_DESC',
              'p_shift_name',
            ]
          )
        );

        setCategories(
          toOpts(
            categoryList,
            [
              'HEC_EMPLOYMENT_CATEGORY_ID',
              'CategoryId',
              'categoryId',
              'HEC_CATEGORY_ID',
            ],
            [
              'HEC_CATEGORY_NAME',
              'CategoryName',
              'categoryName',
              'HEC_CATEGORY_DESC',
              'p_cat_name',
            ]
          )
        );

      } catch (err) {
        console.error(
          '[EmployeeEditModal] Failed to load bank/branch data:',
          err
        );

        setBanks([]);
        setBranches([]);
      } finally {
        if (!cancelled) {
          setBankLoading(false);
        }
      }
    };

    loadBankData();

    return () => {
      cancelled = true;
    };

  }, [isOpen, employee?.HED_EMPLOYEE_ID]);

  // -------------- Dropdown helper --------------------
  const toOpts = (list, idKeys = [], nameKeys = []) => {
    if (!Array.isArray(list)) return [];

    return list
      .map((row) => {
        if (!row || typeof row !== 'object') return null;

        const id = pick(row, [
          ...idKeys,
          'Id',
          'id',
          'Code',
          'code',
          'ID',
          'KEY',
          'key',
        ]);

        const name = pick(row, [
          ...nameKeys,
          'Name',
          'name',
          'Description',
          'description',
          'Desc',
          'desc',
          'Title',
          'title',
          'LABEL',
          'label',
        ]);

        if (id === undefined || id === null || id === '') {
          return null;
        }

        return {
          value: String(id),
          label: String(name || id),
        };
      })
      .filter(Boolean);
  };

  // ─── Qualification helpers ──────────────────────────────────────────────
  const setQualification = (idx, key) => (val) => {
    setForm((f) => {
      const list = [...f.Qualifications];
      list[idx] = { ...list[idx], [key]: val };
      return { ...f, Qualifications: list };
    });
    setDirty(true);
  };

  const addQualification = () =>
    setForm((f) => ({
      ...f,
      Qualifications: [
        ...f.Qualifications,
        {
          _key: `new-${Date.now()}`,
          HEQ_QUALIFICATION_NAME: '',
          HEQ_FIELD_OF_STUDY: '',
          HEQ_INSTITUTE: '',
          HEQ_RESULT: '',
          HEQ_GRADE: '',
          HEQ_GPA: '',
          HEQ_QUALIFICATION_TYPE_ID: 1,
          _isNew: true,
          _isDeleted: false,
        },
      ],
    }));


  const removeQualification = (idx) => {
    if (form.Qualifications[idx]?.HEQ_QUALIFICATION_ID) return;
    setForm((f) => ({ ...f, Qualifications: f.Qualifications.filter((_, i) => i !== idx) }));
  };

  // ─── Save Handler ────────────────────────────────────────────────────────
  const handleSave = async () => {
    const errs = validate(form);

    // Only one primary bank account (some older records have several — the
    // user must untick the extras before saving).
    const primaryBanks = form.Banks.filter((b) => !b._isDeleted && b.HEB_IS_PRIMARY);
    if (primaryBanks.length > 1) {
      errs.banks_primary = `Only one bank account can be primary — ${primaryBanks.length} are ticked. Untick the extra ones.`;
    }

    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      if (errs.banks_primary && Object.keys(errs).length === 1) { setActiveTab('bank'); return; }
      if (errs.HED_FIRST_NAME || errs.HED_LAST_NAME || errs.HED_NIC_NO ||
          errs.HED_EPF_NO || errs.HED_DATE_OF_BIRTH) setActiveTab('overview');
      else if (errs.HED_MOBILE_NO || errs.HED_HOME_NO || errs.HED_OFFICE_NO ||
               errs.HED_PERSONAL_EMAIL || errs.HED_COMPANY_EMAIL ||
               Object.keys(errs).some((k) => k.startsWith('contact_'))) setActiveTab('contact');
      else if (errs.HED_DATE_OF_JOINING || errs.HED_TOTAL_EXPERIANCE) setActiveTab('employment');
      return;
    }

    setLocalSaving(true);
    const serviceNo = employee.HED_SERVICE_NO || employee.p_ced_service_no;
    const employeeId = employee.HED_EMPLOYEE_ID || employee.hedEmployeeId || employee.EmployeeId;

    try {
      // ── 1. Update employee fields ──
      const diff = {};
      const normalize = (v) => (v === null || v === undefined || v === '') ? null : v;

      diff.p_hedServiceNo = serviceNo;

      const ID_KEYS = new Set(['HED_REPORTING_EMPLOYEE_ID']);
      const DATE_KEYS = new Set([
        'HED_DATE_OF_BIRTH', 'HED_DATE_OF_JOINING', 'HED_CONFIRMATION_DATE', 'HED_LEAVING_DATE',
      ]);

      for (const [formKey, apiKey] of Object.entries(FIELD_MAP)) {
        const originalVal = normalize(employee[formKey]);
        const newVal = normalize(form[formKey]);
        if (ID_KEYS.has(formKey)) {
          if (String(originalVal ?? '') !== String(newVal ?? '')) {
            diff[apiKey] = newVal ? Number(newVal) : null;
          }
          continue;
        }
        if (DATE_KEYS.has(formKey)) {
          const origIso = toIso(originalVal ?? '');
          const newIso = toIso(newVal ?? '');
          if (origIso !== newIso) diff[apiKey] = newIso;
          continue;
        }
        if (originalVal !== newVal) {
          let valueToSend = form[formKey];
          if (formKey === 'HED_IS_ACTIVE') {
            valueToSend = Boolean(valueToSend);
          }
          diff[apiKey] = valueToSend;
        }
      }


      if (diff.p_hedEpfNo !== undefined) {
        diff.p_hedEtfNo = diff.p_hedEpfNo;
      }

      // A typed lowercase v is stored and sent as a capital V.
      if (diff.p_hedNicNo !== undefined) {
        diff.p_hedNicNo = normaliseNic(diff.p_hedNicNo);
      }


      if (diff.p_hedIsActive === true) {
        diff.p_hedLeavingDate = '';
        diff.p_hedLeavingReason = '';
      }

      if (Object.keys(diff).length > 1) {
        await employeeService.UpdateEmployee(diff);
      }

      // ── 2. Handle Contacts ──
      const contactsToSave = form.Contacts.filter((c) => !c._isDeleted);

      for (const contact of contactsToSave) {
        if (contact.HEC_CONTACT_ID && !contact._isNew) {
          await employeeService.UpdateEmployeeContact({
            p_hecContactId: contact.HEC_CONTACT_ID,
            p_hedEmployeeId: employeeId,
            p_hecContactName: contact.HEC_CONTACT_NAME || 'N/A',
            p_hecContactNumber: contact.HEC_CONTACT_NUMBER || '',
            p_hecRelationship: contact.HEC_RELATIONSHIP || 'Other',
            p_hecContactType: contact.HEC_CONTACT_TYPE || 'Emergency',
            p_hecAddress: contact.HEC_ADDRESS || '',
          });
        } else if (contact._isNew && (contact.HEC_CONTACT_NAME || contact.HEC_CONTACT_NUMBER)) {
          await employeeService.AddEmployeeContact({
            p_hedEmployeeId: employeeId,
            p_hecContactName: contact.HEC_CONTACT_NAME || 'N/A',
            p_hecContactNumber: contact.HEC_CONTACT_NUMBER || '',
            p_hecRelationship: contact.HEC_RELATIONSHIP || 'Other',
            p_hecContactType: contact.HEC_CONTACT_TYPE || 'Emergency',
            p_hecAddress: contact.HEC_ADDRESS || '',
            p_hecCreatedBy: getLoggedInServiceNo(),
            p_hecIsActive: true,
            p_hecRemarks: '',
          });
        }
      }

      // ── 3. Handle Banks ──
      const banksToSave = form.Banks.filter((b) => !b._isDeleted);


      const originalBankById = {};
      (employee.Banks || []).forEach((b) => {
        originalBankById[String(b.HEB_EMPLOYEE_BANK_ID)] = b;
      });

      const same = (a, b) => String(a ?? '').trim() === String(b ?? '').trim();

      const bankChanged = (bank) => {
        const original = originalBankById[String(bank.HEB_EMPLOYEE_BANK_ID)];
        if (!original) return true;   // no record to compare against — send it
        return !(
          same(bank.HBD_BANK_ID, original.HEB_BANK_ID) &&
          same(bank.HBB_BRANCH_ID, original.HEB_BRANCH_ID) &&
          same(bank.HEB_ACCOUNT_NAME, original.HEB_ACCOUNT_NAME) &&
          same(bank.HEB_ACCOUNT_NUMBER, original.HEB_ACCOUNT_NUMBER) &&
          same(bank.HEB_ACCOUNT_TYPE, original.HEB_ACCOUNT_TYPE)
        );
      };

      // Primary tick changed (true <-> false). Compared with the parsed flag,
      // because the API can send "True"/"False" strings.
      const bankPrimaryChanged = (bank) => {
        const original = originalBankById[String(bank.HEB_EMPLOYEE_BANK_ID)];
        if (!original) return false;
        return Boolean(bank.HEB_IS_PRIMARY) !== isBankPrimary(original);
      };

      // Active/Inactive

      const bankActiveChanged = (bank) => {
        const original = originalBankById[String(bank.HEB_EMPLOYEE_BANK_ID)];
        if (!original) return false;
        return Boolean(bank.HBD_IS_ACTIVE) !== isBankActiveFlag(original);
      };

      for (const bank of banksToSave) {
        if (bank.HEB_EMPLOYEE_BANK_ID && !bank._isNew) {

          if (!bankChanged(bank)) {
            const primaryChanged = bankPrimaryChanged(bank);
            const activeChanged = bankActiveChanged(bank);
            if (!primaryChanged && !activeChanged) continue;   // untouched

            // Only Primary and/or Active changed: send the full row so nothing
            // is blanked, with p_hebIsPrimary true/false. Approval is kept as it is,
            // except Inactive -> Active, which must be approved again.
            const original = originalBankById[String(bank.HEB_EMPLOYEE_BANK_ID)];
            const reactivated = activeChanged && Boolean(bank.HBD_IS_ACTIVE);
            await employeeService.UpdateEmployeeBank({
              p_hebEmployeeBankId: bank.HEB_EMPLOYEE_BANK_ID,
              p_hedEmployeeId: employeeId,
              p_hebBankId: bank.HBD_BANK_ID ? Number(bank.HBD_BANK_ID) : null,
              p_hebBranchId: bank.HBB_BRANCH_ID ? Number(bank.HBB_BRANCH_ID) : null,
              p_hebAccountName: bank.HEB_ACCOUNT_NAME || '',
              p_hebAccountNumber: bank.HEB_ACCOUNT_NUMBER || '',
              p_hebAccountType: bank.HEB_ACCOUNT_TYPE || 'Savings',
              p_hebIsPrimary: Boolean(bank.HEB_IS_PRIMARY),   // true = primary, false = not primary
              p_hebCreatedBy: getLoggedInServiceNo(),
              p_hebIsActive: bank.HBD_IS_ACTIVE ? 1 : 0,
              p_isApproved: reactivated
                ? NEW_BANK_IS_APPROVED
                : (isBankApproved(original) ? BANK_APPROVED : BANK_PENDING),
            });
            continue;
          }

          await employeeService.UpdateEmployeeBank({
            p_hebEmployeeBankId: bank.HEB_EMPLOYEE_BANK_ID,
            p_hedEmployeeId: employeeId,

            p_hebBankId: bank.HBD_BANK_ID ? Number(bank.HBD_BANK_ID) : null,
            p_hebBranchId: bank.HBB_BRANCH_ID ? Number(bank.HBB_BRANCH_ID) : null,
            p_hebAccountName: bank.HEB_ACCOUNT_NAME || '',
            p_hebAccountNumber: bank.HEB_ACCOUNT_NUMBER || '',
            p_hebAccountType: bank.HEB_ACCOUNT_TYPE || 'Savings',
            p_hebIsPrimary: Boolean(bank.HEB_IS_PRIMARY),   // true = primary, false = not primary
            p_hebCreatedBy: getLoggedInServiceNo(),
            p_hebIsActive: bank.HBD_IS_ACTIVE ? 1 : 0,

            p_isApproved: NEW_BANK_IS_APPROVED,
          });
        } else if (bank._isNew && (bank.HEB_ACCOUNT_NUMBER || bank.HBD_BANK_NAME)) {
          await employeeService.AddBankDetails({
            p_hedEmployeeId: employeeId,
            p_hebBankId: bank.HBD_BANK_ID ? Number(bank.HBD_BANK_ID) : null,
            p_hebBranchId: bank.HBB_BRANCH_ID ? Number(bank.HBB_BRANCH_ID) : null,
            p_hebAccountName: bank.HEB_ACCOUNT_NAME || 'N/A',
            p_hebAccountNumber: bank.HEB_ACCOUNT_NUMBER || '',
            p_hebAccountType: bank.HEB_ACCOUNT_TYPE || 'Savings',
            p_hebIsPrimary: bank.HEB_IS_PRIMARY ? 1 : 0,
            p_hebCreatedBy: getLoggedInServiceNo(),
            p_isApproved: NEW_BANK_IS_APPROVED,
          });
        }
      }

      // ── 4. Handle Qualifications ──
      const qualsToSave = form.Qualifications.filter((q) => !q._isDeleted);

      for (const q of qualsToSave) {
        if (q.HEQ_QUALIFICATION_ID && !q._isNew) {
          await employeeService.UpdateEmployeeEdu({
            p_heqQualificationId: q.HEQ_QUALIFICATION_ID,
            p_hedEmployeeId: employeeId,
            p_heqQualificationName: q.HEQ_QUALIFICATION_NAME || '',
            p_heqFieldOfStudy: q.HEQ_FIELD_OF_STUDY || '',
            p_heqInstitute: q.HEQ_INSTITUTE || '',
            p_heqResult: q.HEQ_RESULT || '',
            p_heqGrade: q.HEQ_GRADE || '',
            p_heqGPA: q.HEQ_GPA || '',
          });
        } else if (q._isNew && q.HEQ_QUALIFICATION_NAME) {
          await employeeService.AddEmployeeQualification({
            p_hedEmployeeId: employeeId,
            p_heqQualificationTypeId: q.HEQ_QUALIFICATION_TYPE_ID || 1,
            p_heqQualificationName: q.HEQ_QUALIFICATION_NAME || '',
            p_heqFieldOfStudy: q.HEQ_FIELD_OF_STUDY || '',
            p_heqInstitute: q.HEQ_INSTITUTE || '',
            p_heqCountry: 'Sri Lanka',
            p_heqResult: q.HEQ_RESULT || '',
            p_heqGrade: q.HEQ_GRADE || '',
            p_heqGPA: q.HEQ_GPA || '',
            p_heqIsHighest: false,
            p_heqCreatedBy: getLoggedInServiceNo(),
            p_heqIsActive: true,
          });
        }
      }

      // ── 5. Upload Documents ────────────────────────────────────────────────
      const hasFile = files.profilePic || files.nicDocument || files.birthCertificate || files.qualificationCertificate || files.traineeCertificate || files.otherCertificate;
      if (hasFile && employeeId) {
        const formData = new FormData();
        if (files.profilePic) formData.append('ProfilePic', files.profilePic);
        if (files.nicDocument) formData.append('NIC', files.nicDocument);
        if (files.birthCertificate) formData.append('BirthCertificate', files.birthCertificate);
        if (files.qualificationCertificate) formData.append('QualificationCertificate', files.qualificationCertificate);
        if (files.traineeCertificate) formData.append('TraineeCertificate', files.traineeCertificate);
        if (files.otherCertificate) formData.append('OtherCertificate', files.otherCertificate);

        const docRes = await employeeService.UploadEmployeeDocs(employeeId, formData);
        if (docRes?.data && (docRes.data.StatusCode === 400 || docRes.data.StatusCode === 500)) {
          throw new Error(docRes.data.Message || docRes.data.message || 'Document upload failed on the server.');
        }

        if (files.profilePic) {
          try {
            const base64 = await fileToBase64(files.profilePic);
            const svcNoToSave = serviceNo || employeeId;
            if (svcNoToSave) {
              dispatch(saveEmployeeImage({ serviceNo: String(svcNoToSave).trim(), base64 }));
            }
            if (employeeId && String(employeeId).trim() !== String(svcNoToSave).trim()) {
              dispatch(saveEmployeeImage({ serviceNo: String(employeeId).trim(), base64 }));
            }
          } catch (err) {
            console.error('Failed to convert profile picture to base64 for Redux store:', err);
          }
        }
      }

      if (onSave) await onSave({ ...employee, ...form });

      setDirty(false);
      setLocalSaving(false);
      setLocalSaveError(null);
      onClose();
    } catch (err) {
      setLocalSaving(false);
      const msg = err?.response?.data?.Message || err?.response?.data?.message || err?.message || 'Failed to save changes due to an API error.';
      console.error('Save error:', err);
      setLocalSaveError(msg);
    }
  };

  if (!isOpen || !employee || !form) return null;

  const e = employee;
  const rawSvc = d(e.HED_SERVICE_NO) || d(e.p_ced_service_no) || d(e.HED_EMPLOYEE_ID);
  const uploadedImg = rawSvc ? images[rawSvc] : null;
  const initials = (form.HED_REPORT_NAME || `${form.HED_FIRST_NAME} ${form.HED_LAST_NAME}`)
    .split(/[\s.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase() || '?';
  const directUrl = getEmployeeProfilePicUrl(e);
  const gradient = HEADER_GRADIENT;
  const yearsWorked = calcYears(form.HED_DATE_OF_JOINING);

  // Preview for profile picture: use existing URL if no new file, else use object URL
  const profilePreview = files.profilePic
    ? URL.createObjectURL(files.profilePic)
    : (uploadedImg || directUrl);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={() => !saving && !localSaving && onClose()}
      />

      <div className="relative bg-gray-50 rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* ── Header ── */}
        <div className={`${gradient} px-6 py-5 shrink-0`}>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-xl overflow-hidden shadow-lg ring-2 ring-white/50 shrink-0">
                {profilePreview ? (
                  <img src={profilePreview} alt={initials} className="w-full h-full object-cover" />
                ) : (
                  <div className={`w-full h-full ${gradient} flex items-center justify-center`}>
                    <span className="text-white font-bold text-2xl">{initials}</span>
                  </div>
                )}
              </div>
              <div>
                <h2 className="font-bold text-white text-lg leading-tight">Edit Employee</h2>
                <p className="text-white/80 text-[13px] mt-0.5">
                  {form.HED_FIRST_NAME || form.HED_LAST_NAME
                    ? `${form.HED_FIRST_NAME} ${form.HED_LAST_NAME}`.trim()
                    : (d(e.HED_REPORT_NAME) || d(e.p_report_name) || 'Employee')}
                </p>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {rawSvc && (
                    <span className="inline-flex items-center gap-1 bg-white/20 text-white text-[11px] font-semibold px-2 py-0.5 rounded-full">
                      <Hash size={10} />#{rawSvc}
                    </span>
                  )}
                  {yearsWorked !== null && (
                    <span className="inline-flex items-center gap-1 bg-white/20 text-white text-[11px] font-semibold px-2 py-0.5 rounded-full">
                      <Clock size={10} />{yearsWorked} yr{yearsWorked !== 1 ? 's' : ''}
                    </span>
                  )}
                  {dirty && (
                    <span className="inline-flex items-center gap-1 bg-amber-400/30 text-amber-100 text-[11px] font-bold px-2 py-0.5 rounded-full">
                      Unsaved changes
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={() => !saving && !localSaving && onClose()}
              className="text-white/70 hover:text-white hover:bg-white/20 transition-colors p-2 rounded-lg disabled:opacity-40"
              disabled={saving || localSaving}
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* ── Tabs ── */}
        <div className="flex border-b border-gray-200 bg-white shrink-0 overflow-x-auto">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-1.5 px-4 py-3 text-[12px] font-semibold whitespace-nowrap border-b-2 transition-colors
                ${activeTab === key ? 'border-primary-600 text-primary-700 bg-primary-50/60' : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}
            >
              <Icon size={13} className={activeTab === key ? 'text-primary-600' : ''} />
              {label}
            </button>
          ))}
        </div>

        {/* ── Body ── */}

        <div className="overflow-y-auto h-[clamp(360px,58vh,620px)] px-6 py-5 bg-white">
          {(saveError || localSaveError) && (
            <div className="flex items-center gap-2 px-4 py-3 mb-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-[12px]">
              <AlertCircle size={14} className="shrink-0" />
              <span className="flex-1">{saveError || localSaveError}</span>
              <button onClick={() => setLocalSaveError(null)} className="text-red-400 hover:text-red-600">
                <X size={14} />
              </button>
            </div>
          )}

          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
              <div>
                <SH label="Personal Details" icon={User} />
                <div className="grid grid-cols-3 gap-2">
                  <FI label="Title" value={form.HED_TITLE} onChange={set('HED_TITLE')} placeholder="Mr / Ms" />
                  <div className="col-span-2">
                    <FI label="First Name" value={form.HED_FIRST_NAME} onChange={set('HED_FIRST_NAME')} error={errors.HED_FIRST_NAME} />
                  </div>
                </div>
                <FI label="Middle Name" value={form.HED_MIDDLE_NAME} onChange={set('HED_MIDDLE_NAME')} />
                <FI label="Last Name" value={form.HED_LAST_NAME} onChange={set('HED_LAST_NAME')} error={errors.HED_LAST_NAME} />
                <FI label="Report Name" value={form.HED_REPORT_NAME} onChange={set('HED_REPORT_NAME')} placeholder="Display name used in lists" />
                <FI label="Initials" value={form.HED_INITIALS} onChange={set('HED_INITIALS')} />
                <FI label="Gender" value={form.HED_GENDER} onChange={set('HED_GENDER')} options={GENDER_OPTS} />
                <FI label="Date of Birth *" error={errors.HED_DATE_OF_BIRTH}
                  render={(cls) => (
                    <IsoDateInput
                      value={toIso(form.HED_DATE_OF_BIRTH)}
                      onChange={set('HED_DATE_OF_BIRTH')}
                      max={maxDobIso()}
                      className={cls}
                    />
                  )} />
                <FI label="Marital Status" value={form.HED_MARITAL_STATUS} onChange={set('HED_MARITAL_STATUS')} options={MARITAL_OPTS} />
                <FI label="Religion" value={form.HED_RELIGION} onChange={set('HED_RELIGION')} options={RELIGION_OPTS} />
                <FI label="Blood Group" value={form.HED_BLOOD_GROUP} onChange={set('HED_BLOOD_GROUP')} options={BLOOD_OPTS} />
              </div>
              <div>
                <SH label="Status" icon={Building2} />
                {/* No blank option — an employee is always one or the other,
                    so this doesn't use FI. */}
                <div className="py-1.5">
                  <label className="text-[11px] text-gray-500 font-medium block mb-1">Employment Status</label>
                  <select
                    value={form.HED_IS_ACTIVE ? 'active' : 'inactive'}
                    onChange={(e) => setStatus(e.target.value)}
                    className={`w-full text-[12px] rounded-lg border px-2.5 py-1.5 outline-none transition-colors
                      border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100
                      ${form.HED_IS_ACTIVE ? 'text-emerald-700' : 'text-red-600'}`}
                  >
                    {STATUS_OPTS.map((o) => (
                      <option key={o.value} value={o.value} className="text-gray-700">{o.label}</option>
                    ))}
                  </select>
                </div>

                {!form.HED_IS_ACTIVE && (
                  <>
                    <FI
                      label="Inactive Reason *"
                      value={form.HED_LEAVING_REASON}
                      onChange={set('HED_LEAVING_REASON')}
                      options={INACTIVE_REASON_OPTS}
                      error={errors.HED_LEAVING_REASON}
                    />
                    <FI label="Leaving Date *" error={errors.HED_LEAVING_DATE}
                      render={(cls) => (
                        <IsoDateInput
                          value={form.HED_LEAVING_DATE}
                          onChange={set('HED_LEAVING_DATE')}
                          max={todayIso()}
                          className={cls}
                        />
                      )} />
                  </>
                )}

                <SH label="Identification" icon={Shield} />
                <FI label={tenant?.validation?.nationalIdLabel || 'NIC'} value={form.HED_NIC_NO} onChange={(v) => set('HED_NIC_NO')(sanitiseNic(v))} mono error={errors.HED_NIC_NO} placeholder="200012345678 or 200012345V" />
                <FI label="EPF No. *" value={form.HED_EPF_NO} onChange={(v) => set('HED_EPF_NO')(sanitiseEpf(v))} mono error={errors.HED_EPF_NO} placeholder="max 5" />
                {/* <FI label="ETF No." value={form.HED_ETF_NO} onChange={set('HED_ETF_NO')} mono /> */}
                <FI label="Passport" value={form.HED_PASSPORT_NO} onChange={set('HED_PASSPORT_NO')} mono />

                {/* ── Documents Section ── */}
                <SH label="Documents" icon={Upload} />
                <div className="space-y-3">
                  {/* Profile Picture with preview */}
                  <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl border border-gray-200">
                    <div className="relative shrink-0">
                      <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-white shadow-md ring-2 ring-primary-100 bg-gray-200 flex items-center justify-center">
                        {profilePreview ? (
                          <img src={profilePreview} alt="Profile" className="w-full h-full object-cover" />
                        ) : (
                          <User size={30} className="text-gray-400" />
                        )}
                      </div>
                      <label
                        htmlFor="edit-profile-pic"
                        className="absolute -bottom-1 -right-1 bg-primary-600 hover:bg-primary-700 text-white p-1.5 rounded-full shadow-lg cursor-pointer transition-transform hover:scale-110"
                        title="Upload Profile Picture"
                      >
                        <Upload size={12} />
                      </label>
                      <input
                        id="edit-profile-pic"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => setFile('profilePic', e.target.files?.[0] || null)}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-semibold text-gray-700">Profile Picture</p>
                      <p className="text-[10px] text-gray-500 truncate">
                        {files.profilePic ? files.profilePic.name : (directUrl ? 'Current photo' : 'No photo')}
                      </p>
                      {files.profilePic && (
                        <button
                          type="button"
                          onClick={() => setFile('profilePic', null)}
                          className="text-[10px] text-red-500 hover:text-red-600 flex items-center gap-1 mt-1"
                        >
                          <Trash2 size={10} /> Remove
                        </button>
                      )}
                    </div>
                  </div>

                  {/* NIC Document */}
                  <div className="p-3 bg-gray-50 rounded-xl border border-gray-200">
                    <label className="text-[11px] font-semibold text-gray-700 block mb-1">NIC Copy</label>
                    <div className="flex items-center gap-2">
                      <label
                        htmlFor="edit-nic"
                        className="flex-1 flex items-center justify-between gap-2 border border-gray-300 rounded px-2.5 py-1.5 text-[12px] bg-white cursor-pointer hover:bg-gray-50 transition-colors"
                      >
                        <span className={`truncate ${files.nicDocument ? 'text-gray-700' : 'text-gray-400'}`}>
                          {files.nicDocument ? files.nicDocument.name : 'Choose file...'}
                        </span>
                        <Upload size={13} className="text-gray-400 shrink-0" />
                      </label>
                      <input id="edit-nic" type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => setFile('nicDocument', e.target.files?.[0] || null)} />
                      {files.nicDocument && (
                        <button onClick={() => setFile('nicDocument', null)} className="text-gray-400 hover:text-red-500">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Birth Certificate */}
                  <div className="p-3 bg-gray-50 rounded-xl border border-gray-200">
                    <label className="text-[11px] font-semibold text-gray-700 block mb-1">Birth Certificate</label>
                    <div className="flex items-center gap-2">
                      <label
                        htmlFor="edit-birthcert"
                        className="flex-1 flex items-center justify-between gap-2 border border-gray-300 rounded px-2.5 py-1.5 text-[12px] bg-white cursor-pointer hover:bg-gray-50 transition-colors"
                      >
                        <span className={`truncate ${files.birthCertificate ? 'text-gray-700' : 'text-gray-400'}`}>
                          {files.birthCertificate ? files.birthCertificate.name : 'Choose file...'}
                        </span>
                        <Upload size={13} className="text-gray-400 shrink-0" />
                      </label>
                      <input id="edit-birthcert" type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => setFile('birthCertificate', e.target.files?.[0] || null)} />
                      {files.birthCertificate && (
                        <button onClick={() => setFile('birthCertificate', null)} className="text-gray-400 hover:text-red-500">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Qualification Certificate */}
                  <div className="p-3 bg-gray-50 rounded-xl border border-gray-200">
                    <label className="text-[11px] font-semibold text-gray-700 block mb-1">Qualification Certificate</label>
                    <div className="flex items-center gap-2">
                      <label
                        htmlFor="edit-qualcert"
                        className="flex-1 flex items-center justify-between gap-2 border border-gray-300 rounded px-2.5 py-1.5 text-[12px] bg-white cursor-pointer hover:bg-gray-50 transition-colors"
                      >
                        <span className={`truncate ${files.qualificationCertificate ? 'text-gray-700' : 'text-gray-400'}`}>
                          {files.qualificationCertificate ? files.qualificationCertificate.name : 'Choose file...'}
                        </span>
                        <Upload size={13} className="text-gray-400 shrink-0" />
                      </label>
                      <input id="edit-qualcert" type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => setFile('qualificationCertificate', e.target.files?.[0] || null)} />
                      {files.qualificationCertificate && (
                        <button onClick={() => setFile('qualificationCertificate', null)} className="text-gray-400 hover:text-red-500">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Trainee Certificate */}
                  <div className="p-3 bg-gray-50 rounded-xl border border-gray-200">
                    <label className="text-[11px] font-semibold text-gray-700 block mb-1">Trainee Certificate</label>
                    <div className="flex items-center gap-2">
                      <label
                        htmlFor="edit-traineecert"
                        className="flex-1 flex items-center justify-between gap-2 border border-gray-300 rounded px-2.5 py-1.5 text-[12px] bg-white cursor-pointer hover:bg-gray-50 transition-colors"
                      >
                        <span className={`truncate ${files.traineeCertificate ? 'text-gray-700' : 'text-gray-400'}`}>
                          {files.traineeCertificate ? files.traineeCertificate.name : 'Choose file...'}
                        </span>
                        <Upload size={13} className="text-gray-400 shrink-0" />
                      </label>
                      <input id="edit-traineecert" type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => setFile('traineeCertificate', e.target.files?.[0] || null)} />
                      {files.traineeCertificate && (
                        <button onClick={() => setFile('traineeCertificate', null)} className="text-gray-400 hover:text-red-500">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Other Certificates */}
                  <div className="p-3 bg-gray-50 rounded-xl border border-gray-200">
                    <label className="text-[11px] font-semibold text-gray-700 block mb-1">Other Certificates</label>
                    <div className="flex items-center gap-2">
                      <label
                        htmlFor="edit-othercert"
                        className="flex-1 flex items-center justify-between gap-2 border border-gray-300 rounded px-2.5 py-1.5 text-[12px] bg-white cursor-pointer hover:bg-gray-50 transition-colors"
                      >
                        <span className={`truncate ${files.otherCertificate ? 'text-gray-700' : 'text-gray-400'}`}>
                          {files.otherCertificate ? files.otherCertificate.name : 'Choose file...'}
                        </span>
                        <Upload size={13} className="text-gray-400 shrink-0" />
                      </label>
                      <input id="edit-othercert" type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => setFile('otherCertificate', e.target.files?.[0] || null)} />
                      {files.otherCertificate && (
                        <button onClick={() => setFile('otherCertificate', null)} className="text-gray-400 hover:text-red-500">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
              <div>
                <SH label="Phone Numbers" icon={Phone} />
                <FI label="Mobile" value={form.HED_MOBILE_NO} onChange={(v) => set('HED_MOBILE_NO')(sanitisePhone(v))} error={errors.HED_MOBILE_NO} placeholder={tenant?.validation?.phoneExample} />
                <FI label="Home Phone" value={form.HED_HOME_NO} onChange={(v) => set('HED_HOME_NO')(sanitisePhone(v))} error={errors.HED_HOME_NO} placeholder={tenant?.validation?.landlineExample} />
                <FI label="Office Phone" value={form.HED_OFFICE_NO} onChange={(v) => set('HED_OFFICE_NO')(sanitisePhone(v))} error={errors.HED_OFFICE_NO} />

                <SH label="Email" icon={Mail} />
                <FI label="Personal Email" value={form.HED_PERSONAL_EMAIL} onChange={set('HED_PERSONAL_EMAIL')} error={errors.HED_PERSONAL_EMAIL} placeholder={tenant?.validation?.emailExample} />
                <FI label="Company Email" value={form.HED_COMPANY_EMAIL} onChange={set('HED_COMPANY_EMAIL')} error={errors.HED_COMPANY_EMAIL} />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2 mt-4">
                  <div className="flex items-center gap-2">
                    <PhoneCall size={12} className="text-primary-600" />
                    <span className="text-[10px] font-bold text-primary-700 uppercase tracking-wider">Emergency Contacts</span>
                  </div>
                  <button onClick={addContact} type="button" className="flex items-center gap-1 text-[11px] font-semibold text-primary-600 hover:text-primary-800">
                    <Plus size={12} /> Add
                  </button>
                </div>
                {form.Contacts.filter((c) => !c._isDeleted).length === 0 && (
                  <p className="text-[11px] text-gray-400 italic">No emergency contacts. Click Add to create one.</p>
                )}
                {form.Contacts.map((c, i) => {
                  if (c._isDeleted) return null;
                  return (
                    <div key={c._key} className="mb-3 pl-2 border-l-2 border-primary-100 relative">
                      {!c.HEC_CONTACT_ID && (
                        <button onClick={() => removeContact(i)} type="button" title="Remove this new contact" className="absolute right-0 top-1 text-gray-300 hover:text-red-500">
                          <Trash2 size={13} />
                        </button>
                      )}
                      <FI label="Name" value={c.HEC_CONTACT_NAME} onChange={setContact(i, 'HEC_CONTACT_NAME')} />
                      <FI label="Number" value={c.HEC_CONTACT_NUMBER} onChange={(v) => setContact(i, 'HEC_CONTACT_NUMBER')(sanitisePhone(v))} error={errors[`contact_${i}_number`]} />
                      <FI label="Relationship" value={c.HEC_RELATIONSHIP} onChange={setContact(i, 'HEC_RELATIONSHIP')} options={RELATIONSHIP_OPTS} />
                      <FI label="Address" value={c.HEC_ADDRESS} onChange={setContact(i, 'HEC_ADDRESS')} />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {activeTab === 'employment' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
              <div>
                <SH label="Organisation" icon={Building2} />
                <FI
                  label="Location / Cost Center"
                  value={form.HED_LOCATION_ID || form.HED_COSTCENTER}
                  onChange={(val) => {
                    const matchedLoc = locations.find(l => String(l.value) === String(val));
                    setForm(f => ({
                      ...f,
                      HED_LOCATION_ID: val,
                      HED_COSTCENTER: matchedLoc?.label || val,
                    }));
                    setDirty(true);
                  }}
                  options={locations}
                  loading={employmentLoading}
                  placeholder="Select Location"
                />
                <FI
                  label="Department"
                  value={form.HDD_DEPARTMENT_ID || form.HDD_DEPARTMENT_NAME}
                  onChange={(val) => {
                    const matchedDept = departments.find(d => String(d.value) === String(val));
                    setForm(f => ({
                      ...f,
                      HDD_DEPARTMENT_ID: val,
                      HDD_DEPARTMENT_NAME: matchedDept?.label || val,
                    }));
                    setDirty(true);
                  }}
                  options={departments}
                  loading={bankLoading || employmentLoading}
                  placeholder="Select Department"
                />
                {/* <FI label="Designation" value={form.HED_DESIGNATION_NAME} onChange={set('HED_DESIGNATION_NAME')} /> */}

                <FI
                  label="Designation"
                  value={form.HED_DESIGNATION_ID}
                  onChange={set('HED_DESIGNATION_ID')}
                  options={designations}
                  loading={employmentLoading}
                  placeholder="Select Designation"
                />

                <FI
                  label="Grade"
                  value={form.HED_GRADE_ID}
                  onChange={set('HED_GRADE_ID')}
                  options={grades}
                  loading={employmentLoading}
                  placeholder="Select Grade"
                />

                <FI
                  label="Employee Category"
                  value={form.HED_EMPLOYMENT_CATEGORY_ID || form.HEC_CATEGORY_NAME}
                  onChange={(val) => {
                    const matchedCat = categories.find(c => String(c.value) === String(val));
                    setForm(f => ({
                      ...f,
                      HED_EMPLOYMENT_CATEGORY_ID: val,
                      HEC_CATEGORY_NAME: matchedCat?.label || val,
                    }));
                    setDirty(true);
                  }}
                  options={categories}
                  loading={employmentLoading}
                  placeholder="Select Category"
                />

                <FI
                  label="Shift"
                  value={form.HED_SHIFT_ID}
                  onChange={set('HED_SHIFT_ID')}
                  options={shifts}
                  loading={employmentLoading}
                  placeholder="Select Shift"
                />

                <FI
                  label="Reporting Officer"
                  value={form.HED_REPORTING_EMPLOYEE_ID}
                  onChange={set('HED_REPORTING_EMPLOYEE_ID')}
                  options={reportingOptions}
                  loading={employmentLoading}
                  placeholder="Select Reporting Officer"
                />

                <FI label="Notice Period (days)" value={form.HED_NOTICE_PERIOD_DAYS} onChange={set('HED_NOTICE_PERIOD_DAYS')} type="number" />
                <FI label="OT Rate (LKR)" value={form.HED_OT_RATE} onChange={set('HED_OT_RATE')} type="number" />
                <FI label="Total Experience" error={errors.HED_TOTAL_EXPERIANCE}
                  render={(cls) => (
                    <ExperienceInput
                      value={form.HED_TOTAL_EXPERIANCE}
                      onChange={set('HED_TOTAL_EXPERIANCE')}
                      inputClassName={cls}
                    />
                  )} />
              </div>
              <div>
                <SH label="Key Dates" icon={Calendar} />
                <FI label="Join Date" error={errors.HED_DATE_OF_JOINING}
                  render={(cls) => (
                    <IsoDateInput
                      value={toIso(form.HED_DATE_OF_JOINING)}
                      onChange={set('HED_DATE_OF_JOINING')}
                      max={todayIso()}
                      className={cls}
                    />
                  )} />
                {/* <FI label="Confirmation" value={form.HED_CONFIRMATION_DATE} onChange={set('HED_CONFIRMATION_DATE')} type="date" /> */}

                <SH label="Notes" icon={Briefcase} />
                <div className="py-1.5">
                  <label className="text-[11px] text-gray-500 font-medium block mb-1">Remarks</label>
                  <textarea
                    className="w-full text-[12px] rounded-lg border border-gray-200 px-2.5 py-1.5 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 min-h-[80px]"
                    value={form.HED_REMARKS}
                    onChange={(e) => set('HED_REMARKS')(e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'bank' && (
            <div className="space-y-4">
              {errors.banks_primary && (
                <div className="flex items-start gap-2 p-3 rounded-lg border border-red-200 bg-red-50 text-[12px] text-red-600">
                  <AlertCircle size={14} className="shrink-0 mt-0.5" />
                  <span>{errors.banks_primary}</span>
                </div>
              )}
              <div className="flex justify-end">
                <button onClick={addBank} type="button" className="flex items-center gap-1 text-[11px] font-semibold text-primary-600 hover:text-primary-800">
                  <Plus size={12} /> Add bank account
                </button>
              </div>
              {form.Banks.filter((b) => !b._isDeleted).length === 0 && (
                <p className="text-[12px] text-gray-400 italic">No bank accounts on record.</p>
              )}
              {form.Banks.map((b, i) => {
                if (b._isDeleted) return null;
                return (
                  // <div key={b._key} className="p-4 rounded-xl border border-gray-100 bg-gray-50 relative">
                  <div key={b._key} className={`p-4 rounded-xl border border-gray-100 bg-gray-50 relative ${b.HBD_IS_ACTIVE === false ? 'opacity-60' : ''}`}>
                    {!b.HEB_EMPLOYEE_BANK_ID && (
                      <button onClick={() => removeBank(i)} type="button" title="Remove this new bank account" className="absolute right-3 top-3 text-gray-300 hover:text-red-500">
                        <Trash2 size={14} />
                      </button>
                    )}
                    <div className="flex items-center gap-2 mb-3">
                      <CreditCard size={14} className="text-primary-500" />
                      <span className="text-[12px] font-bold text-gray-700">Bank Account {i + 1}</span>
                      <label className="flex items-center gap-1.5 ml-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={b.HEB_IS_PRIMARY}
                          onChange={(e) => setBank(i, 'HEB_IS_PRIMARY')(e.target.checked)}
                          className="w-3.5 h-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-400"
                        />
                        <span className="text-[11px] text-gray-500">Primary</span>
                      </label>
                      <label className="flex items-center gap-1.5 ml-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={b.HBD_IS_ACTIVE !== false}
                          onChange={(e) => setBank(i, 'HBD_IS_ACTIVE')(e.target.checked)}
                          className="w-3.5 h-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-400"
                        />
                        <span className={`text-[11px] ${b.HBD_IS_ACTIVE === false ? 'text-red-500 font-semibold' : 'text-gray-500'}`}>
                          {b.HBD_IS_ACTIVE === false ? 'Inactive' : 'Active'}
                        </span>
                      </label>
                      {b._wasActive === false && b.HBD_IS_ACTIVE !== false && (
                        <span className="ml-2 inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                          Needs approval after saving
                        </span>
                      )}
                    </div>
                    {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                      <div>
                        <FI label="Bank" value={b.HBD_BANK_NAME} onChange={setBank(i, 'HBD_BANK_NAME')} />
                        <FI label="Branch" value={b.HBB_BRANCH_NAME} onChange={setBank(i, 'HBB_BRANCH_NAME')} />
                      </div>
                      <div>
                        <FI label="Account Name" value={b.HEB_ACCOUNT_NAME} onChange={setBank(i, 'HEB_ACCOUNT_NAME')} />
                        <FI label="Account Number" value={b.HEB_ACCOUNT_NUMBER} onChange={setBank(i, 'HEB_ACCOUNT_NUMBER')} mono />
                        <FI label="Account Type" value={b.HEB_ACCOUNT_TYPE} onChange={setBank(i, 'HEB_ACCOUNT_TYPE')} />
                      </div>
                    </div> */}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                      <div>
                        <FI
                          label="Bank"
                          value={b.HBD_BANK_ID}
                          onChange={(value) => {
                            setForm((f) => {
                              const list = [...f.Banks];

                              list[i] = {
                                ...list[i],
                                HBD_BANK_ID: value,
                                HBB_BRANCH_ID: '',
                              };

                              return {
                                ...f,
                                Banks: list,
                              };
                            });

                            setDirty(true);
                          }}
                          options={banks}
                        />

                        <FI
                          label="Branch"
                          value={b.HBB_BRANCH_ID}
                          onChange={setBank(i, 'HBB_BRANCH_ID')}
                          options={
                            b.HBD_BANK_ID
                              ? branches.filter(
                                  (branch) => branch.bankId === String(b.HBD_BANK_ID)
                                )
                              : []
                          }
                        />
                      </div>

                      <div>
                        <FI
                          label="Account Name"
                          value={b.HEB_ACCOUNT_NAME}
                          onChange={setBank(i, 'HEB_ACCOUNT_NAME')}
                        />

                        <FI
                          label="Account Number"
                          value={b.HEB_ACCOUNT_NUMBER}
                          onChange={setBank(i, 'HEB_ACCOUNT_NUMBER')}
                          mono
                        />

                        <FI
                          label="Account Type"
                          value={b.HEB_ACCOUNT_TYPE}
                          onChange={setBank(i, 'HEB_ACCOUNT_TYPE')}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {activeTab === 'qualify' && (
            <div className="space-y-4">
              <div className="flex justify-end">
                <button onClick={addQualification} type="button" className="flex items-center gap-1 text-[11px] font-semibold text-primary-600 hover:text-primary-800">
                  <Plus size={12} /> Add Qualification
                </button>
              </div>
              {form.Qualifications.filter((q) => !q._isDeleted).length === 0 && (
                <p className="text-[12px] text-gray-400 italic">No qualifications on record.</p>
              )}
              {form.Qualifications.map((q, i) => {
                if (q._isDeleted) return null;
                return (
                  <div key={q._key} className="p-4 rounded-xl border border-gray-100 bg-gray-50 relative">
                    {!q.HEQ_QUALIFICATION_ID && (
                      <button onClick={() => removeQualification(i)} type="button" title="Remove this new qualification" className="absolute right-3 top-3 text-gray-300 hover:text-red-500">
                        <Trash2 size={14} />
                      </button>
                    )}
                    <div className="flex items-center gap-2 mb-3">
                      <Award size={14} className="text-primary-500" />
                      <span className="text-[12px] font-bold text-gray-700">Qualification {i + 1}</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                      <div>
                        <FI label="Qualification Name" value={q.HEQ_QUALIFICATION_NAME} onChange={setQualification(i, 'HEQ_QUALIFICATION_NAME')} />
                        <FI label="Field of Study" value={q.HEQ_FIELD_OF_STUDY} onChange={setQualification(i, 'HEQ_FIELD_OF_STUDY')} />
                        <FI label="Institute" value={q.HEQ_INSTITUTE} onChange={setQualification(i, 'HEQ_INSTITUTE')} />
                      </div>
                      <div>
                        <FI label="Result" value={q.HEQ_RESULT} onChange={setQualification(i, 'HEQ_RESULT')} />
                        <FI label="Grade" value={q.HEQ_GRADE} onChange={setQualification(i, 'HEQ_GRADE')} />
                        <FI label="GPA" value={q.HEQ_GPA} onChange={setQualification(i, 'HEQ_GPA')} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="shrink-0 bg-gray-50 border-t border-gray-200 px-6 py-3 flex items-center justify-between rounded-b-xl">
          <p className="text-[10px] text-gray-400">
            Press <kbd className="px-1 py-0.5 rounded border border-gray-300 font-mono text-[10px] bg-white">ESC</kbd> to close
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => !saving && !localSaving && onClose()}
              disabled={saving || localSaving}
              className="h-9 px-4 text-xs font-semibold rounded-lg bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || localSaving}
              className="flex items-center gap-2 h-9 px-5 text-xs font-semibold rounded-lg bg-primary-600 hover:bg-primary-700 text-white shadow-sm transition-colors disabled:opacity-50"
            >
              {saving || localSaving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              {saving || localSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}